import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';
const REGISTRATION_API = process.env.REACT_APP_REGISTRATION_API_URL || 'http://localhost:8085';

const csvValue = (value) => {
  const cell = value === null || value === undefined || value === '' ? '' : String(value);
  if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
};

const downloadCsv = (filename, headers, rows) => {
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(csvValue).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const downloadUrl = URL.createObjectURL(blob);

  link.setAttribute('href', downloadUrl);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
};

const formatDateTime = (dateString) => {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleString();
};

const normalizeIdSet = (ids = []) => new Set(ids.map(id => String(id)));

const slugify = (value) => String(value || 'chair')
  .trim()
  .replace(/[^a-z0-9]+/gi, '_')
  .replace(/^_+|_+$/g, '')
  .toLowerCase() || 'chair';

const getPaperDisplayId = (paper) => paper?.paperId || '';
const getRegistrationDisplayId = (paper) =>
  paper?.registrationDisplayId || paper?.registrationNumber || '';
const getTrackDisplayName = (paper) => paper?.trackName || paper?.track || '';

const ChairManagement = () => {
  const [chairs, setChairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingChairId, setDownloadingChairId] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingChair, setEditingChair] = useState(null);
  const [conferences, setConferences] = useState([]);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    conferenceIds: [],
    trackIds: []
  });

  useEffect(() => {
    fetchChairs();
    fetchConferences();
  }, []);

  const fetchChairs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFERENCE_API}/api/submission-reviewers/role/CHAIR`);
      if (response.ok) {
        const data = await response.json();
        setChairs(data);
      }
    } catch (error) {
      console.error('Error fetching chairs:', error);
      showAlert('Failed to fetch chairs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchConferences = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API}/active`);
      if (response.ok) {
        const data = await response.json();
        setConferences(data);
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };



  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleOpenModal = (chair = null) => {
    if (chair) {
      setEditingChair(chair);
      setFormData({
        fullName: chair.fullName,
        email: chair.email,
        conferenceIds: chair.conferenceIds || [],
        trackIds: chair.trackIds || []
      });
    } else {
      setEditingChair(null);
      setFormData({
        fullName: '',
        email: '',
        conferenceIds: [],
        trackIds: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingChair(null);
    setFormData({
      fullName: '',
      email: '',
      conferenceIds: [],
      trackIds: []
    });
    setAvailableTracks([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConferenceToggle = (conferenceId) => {
    setFormData(prev => {
      const conferenceIds = prev.conferenceIds.includes(conferenceId)
        ? prev.conferenceIds.filter(id => id !== conferenceId)
        : [...prev.conferenceIds, conferenceId];
      return { ...prev, conferenceIds };
    });
  };

  const handleTrackToggle = (trackId) => {
    setFormData(prev => {
      const trackIds = prev.trackIds.includes(trackId)
        ? prev.trackIds.filter(id => id !== trackId)
        : [...prev.trackIds, trackId];
      return { ...prev, trackIds };
    });
  };

  const fetchTracksForConferences = async (conferenceIds) => {
    if (!conferenceIds || conferenceIds.length === 0) {
      setAvailableTracks([]);
      return;
    }

    setTracksLoading(true);
    try {
      const responses = await Promise.all(
        conferenceIds.map(async (conferenceId) => {
          const response = await fetch(`${CONFERENCE_API}/api/tracks/conference/${conferenceId}`);
          if (!response.ok) {
            return [];
          }
          const tracks = await response.json();
          const conference = conferences.find(c => c.id === conferenceId);
          return (Array.isArray(tracks) ? tracks : []).map(track => ({
            ...track,
            conferenceId,
            conferenceName: conference?.name || 'Conference'
          }));
        })
      );

      const mergedTracks = responses.flat();
      setAvailableTracks(mergedTracks);

      // Keep only track selections that are valid for selected conferences
      const validTrackIds = new Set(mergedTracks.map(track => track.id));
      setFormData(prev => ({
        ...prev,
        trackIds: prev.trackIds.filter(id => validTrackIds.has(id))
      }));
    } catch (error) {
      console.error('Error fetching tracks for conferences:', error);
      setAvailableTracks([]);
    } finally {
      setTracksLoading(false);
    }
  };

  useEffect(() => {
    fetchTracksForConferences(formData.conferenceIds);
  }, [formData.conferenceIds, conferences]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const chairData = {
        fullName: formData.fullName,
        email: formData.email,
        role: 'CHAIR',
        conferenceIds: formData.conferenceIds,
        trackIds: formData.trackIds
      };

      if (!formData.conferenceIds || formData.conferenceIds.length === 0) {
        showAlert('Please select at least one conference', 'error');
        setLoading(false);
        return;
      }

      if (!formData.trackIds || formData.trackIds.length === 0) {
        showAlert('Please select at least one stream', 'error');
        setLoading(false);
        return;
      }

      const url = editingChair
        ? `${CONFERENCE_API}/api/submission-reviewers/${editingChair.id}`
        : `${CONFERENCE_API}/api/submission-reviewers`;

      const method = editingChair ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(chairData)
      });

      if (response.ok) {
        showAlert(editingChair ? 'Chair updated successfully!' : 'Chair added successfully! Password setup email sent.', 'success');
        handleCloseModal();
        fetchChairs();
      } else {
        const errorText = await response.text();
        showAlert(errorText || 'Failed to save chair', 'error');
      }
    } catch (error) {
      console.error('Error saving chair:', error);
      showAlert('Failed to save chair', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (chairId) => {
    if (!window.confirm('Are you sure you want to delete this chairperson?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${CONFERENCE_API}/api/submission-reviewers/${chairId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showAlert('Chair deleted successfully', 'success');
        fetchChairs();
      } else {
        showAlert('Failed to delete chair', 'error');
      }
    } catch (error) {
      console.error('Error deleting chair:', error);
      showAlert('Failed to delete chair', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getChairConferences = (chair) => {
    const conferencesById = new Map(conferences.map(conf => [String(conf.id), conf]));
    const conferenceIds = chair.conferenceIds || [];

    if (conferenceIds.length > 0) {
      return conferenceIds
        .map(conferenceId => {
          const conference = conferencesById.get(String(conferenceId));
          return {
            id: conferenceId,
            name: conference?.name || chair.conferenceNames?.[conferenceIds.indexOf(conferenceId)]
          };
        })
        .filter(conference => conference.name);
    }

    return (chair.conferenceNames || []).map(name => ({ id: null, name }));
  };

  const getPaperRowsForChair = async (chair) => {
    const assignedConferences = getChairConferences(chair);
    const assignedTrackIds = normalizeIdSet(chair.trackIds || []);
    const filterByTracks = assignedTrackIds.size > 0;

    if (assignedConferences.length === 0) {
      return [];
    }

    const paperGroups = await Promise.all(
      assignedConferences.map(async (conference) => {
        const response = await fetch(`${REGISTRATION_API}/api/papers/conference/${encodeURIComponent(conference.name)}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch papers for ${conference.name}`);
        }

        const papers = await response.json();
        return (Array.isArray(papers) ? papers : []).map(paper => ({
          ...paper,
          conference: paper.conference || conference.name
        }));
      })
    );

    const papers = paperGroups.flat();
    const scopedPapers = filterByTracks
      ? papers.filter(paper => assignedTrackIds.has(String(paper.trackId)))
      : papers;

    return scopedPapers.map(paper => [
      chair.fullName || '',
      chair.email || '',
      paper.conference || '',
      getPaperDisplayId(paper),
      getRegistrationDisplayId(paper),
      paper.title || '',
      paper.abstractText || '',
      paper.authorName || '',
      getTrackDisplayName(paper),
      paper.presentationMode || '',
      paper.status || '',
      formatDateTime(paper.createdAt),
      formatDateTime(paper.updatedAt),
      paper.fileId || ''
    ]);
  };

  const handleDownloadPapers = async (chair) => {
    if (downloadingChairId) {
      return;
    }

    setDownloadingChairId(chair.id);
    showAlert(`Preparing paper list for ${chair.fullName}...`, 'success');

    try {
      const rows = await getPaperRowsForChair(chair);

      if (rows.length === 0) {
        showAlert('No papers found for this chairperson assignment', 'error');
        return;
      }

      const headers = [
        'Chair Name',
        'Chair Email',
        'Conference',
        'Paper ID',
        'Registration ID',
        'Title',
        'Abstract',
        'Author',
        'Track',
        'Presentation Mode',
        'Status',
        'Submitted On',
        'Updated On',
        'File ID'
      ];
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `papers_${slugify(chair.fullName || chair.email)}_${timestamp}.csv`;

      downloadCsv(filename, headers, rows);
      showAlert(`Downloaded ${rows.length} papers for ${chair.fullName}`, 'success');
    } catch (error) {
      console.error('Error downloading chair paper list:', error);
      showAlert(error.message || 'Failed to download chair paper list', 'error');
    } finally {
      setDownloadingChairId(null);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2>Chairperson Management</h2>
          <p style={{ color: '#666', marginTop: '8px' }}>
            Manage conference chairpersons
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          + Add Chairperson
        </button>
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      {loading && !showModal ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading...</p>
        </div>
      ) : chairs.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>No Chairpersons Found</h3>
          <p>Click "Add Chairperson" to create the first chairperson.</p>
        </div>
      ) : (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '10%' }} />
              <col style={{ width: '29%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '36%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Conferences</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Streams</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {chairs.map(chair => (
                <tr key={chair.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px', fontWeight: '500', verticalAlign: 'middle' }}>{chair.fullName}</td>
                  <td style={{ padding: '12px', verticalAlign: 'middle', overflowWrap: 'anywhere' }}>{chair.email}</td>
                  <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                    {chair.conferenceNames && chair.conferenceNames.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {chair.conferenceNames.map((name, index) => (
                          <span key={index} style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            fontSize: '11px'
                          }}>
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                    {chair.trackNames && chair.trackNames.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {chair.trackNames.map((name, index) => (
                          <span key={index} style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#6f42c1',
                            color: 'white',
                            fontSize: '11px'
                          }}>
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : 'All streams in selected conferences'}
                  </td>
                  <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                    <button
                      onClick={() => handleDownloadPapers(chair)}
                      disabled={!!downloadingChairId}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: downloadingChairId ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        opacity: downloadingChairId ? 0.6 : 1,
                        minWidth: '122px'
                      }}
                    >
                      {downloadingChairId === chair.id ? 'Downloading...' : 'Download Papers'}
                    </button>
                    <button
                      onClick={() => handleOpenModal(chair)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        minWidth: '58px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(chair.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        minWidth: '62px'
                      }}
                    >
                      Delete
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#333' }}>
              {editingChair ? 'Edit Chairperson' : 'Add Chairperson'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                  Full Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter full name"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                  Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingChair}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: editingChair ? '#f5f5f5' : 'white'
                  }}
                  placeholder="chair@example.com"
                />
                {editingChair && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Email cannot be changed
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                  Conferences <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '15px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: '#f9f9f9'
                }}>
                  {conferences.length === 0 ? (
                    <p style={{ margin: 0, color: '#666' }}>No conferences available</p>
                  ) : (
                    conferences.map(conf => (
                      <div key={conf.id} style={{ marginBottom: '10px' }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '8px',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          border: formData.conferenceIds.includes(conf.id) ? '2px solid #007bff' : '1px solid #ddd'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.conferenceIds.includes(conf.id)}
                            onChange={() => handleConferenceToggle(conf.id)}
                            style={{ marginRight: '10px', cursor: 'pointer' }}
                          />
                          <span style={{ fontWeight: formData.conferenceIds.includes(conf.id) ? 'bold' : 'normal' }}>
                            {conf.name}
                          </span>
                        </label>
                      </div>
                    ))
                  )}
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Select at least one conference.
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                  Streams (Tracks) <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '15px',
                  maxHeight: '220px',
                  overflowY: 'auto',
                  backgroundColor: '#f9f9f9'
                }}>
                  {tracksLoading ? (
                    <p style={{ margin: 0, color: '#666' }}>Loading streams...</p>
                  ) : availableTracks.length === 0 ? (
                    <p style={{ margin: 0, color: '#666' }}>
                      {formData.conferenceIds.length === 0
                        ? 'Select conferences to load streams'
                        : 'No streams found for selected conferences'}
                    </p>
                  ) : (
                    availableTracks.map(track => (
                      <div key={track.id} style={{ marginBottom: '10px' }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '8px',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          border: formData.trackIds.includes(track.id) ? '2px solid #6f42c1' : '1px solid #ddd'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.trackIds.includes(track.id)}
                            onChange={() => handleTrackToggle(track.id)}
                            style={{ marginRight: '10px', cursor: 'pointer' }}
                          />
                          <div>
                            <div style={{ fontWeight: formData.trackIds.includes(track.id) ? 'bold' : 'normal' }}>
                              {track.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {track.conferenceName}
                            </div>
                          </div>
                        </label>
                      </div>
                    ))
                  )}
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Select one or more streams. Chairperson assignment requires at least one stream.
                </p>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'flex-end',
                borderTop: '1px solid #f0f0f0',
                paddingTop: '20px'
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? 'Saving...' : editingChair ? 'Update' : 'Add Chairperson'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChairManagement;
